/**
 * Employee Self-Update Request contracts — M03 HRIS Wave 8.3.
 *
 * Employees submit proposed changes to their own HRIS data; HR/manager
 * reviews and approves. On approve the service applies the JSONB payload
 * to the target row inside a DB transaction.
 *
 * The `target_table` enum identifies which sub-table the change targets.
 * `proposed_changes` is opaque JSONB on the wire; the server validates it
 * against the relevant module's schema (NewEmployeeAddressSchema,
 * UpdateEmployeeSchema, etc.) before applying.
 */
import { z } from 'zod';

import { TenantIdSchema, UserIdSchema } from '../common/identifiers.js';

import { EmployeeIdSchema } from './employee.js';

// ---------------------------------------------------------------------------
// Enum + identifier
// ---------------------------------------------------------------------------

export const SelfUpdateRequestIdSchema = z.string().uuid().brand<'SelfUpdateRequestId'>();
export type SelfUpdateRequestId = z.infer<typeof SelfUpdateRequestIdSchema>;

export const SelfUpdateTargetTableSchema = z.enum([
  'employee',
  'address',
  'emergency_contact',
  'dependent',
  'bank_account',
]);
export type SelfUpdateTargetTable = z.infer<typeof SelfUpdateTargetTableSchema>;

export const SelfUpdateRequestStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type SelfUpdateRequestStatus = z.infer<typeof SelfUpdateRequestStatusSchema>;

// ---------------------------------------------------------------------------
// Row shape (output)
// ---------------------------------------------------------------------------

export const EmployeeSelfUpdateRequestSchema = z.object({
  id: SelfUpdateRequestIdSchema,
  tenantId: TenantIdSchema,
  employeeId: EmployeeIdSchema,
  targetTable: SelfUpdateTargetTableSchema,
  targetId: z.string().uuid().nullable(),
  proposedChanges: z.record(z.string(), z.unknown()),
  reason: z.string().nullable(),
  status: SelfUpdateRequestStatusSchema,
  reviewerId: UserIdSchema.nullable(),
  reviewedAt: z.string().datetime({ offset: true }).nullable(),
  reviewerNote: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type EmployeeSelfUpdateRequest = z.infer<typeof EmployeeSelfUpdateRequestSchema>;

// ---------------------------------------------------------------------------
// POST /employees/me/update-requests
// ---------------------------------------------------------------------------

export const CreateSelfUpdateRequestInputSchema = z.object({
  targetTable: SelfUpdateTargetTableSchema,
  // null = creates a brand-new sub-row (e.g. add a new dependent)
  targetId: z.string().uuid().nullable().optional(),
  proposedChanges: z.record(z.string(), z.unknown()),
  reason: z.string().max(1000).nullable().optional(),
});
export type CreateSelfUpdateRequestInput = z.infer<typeof CreateSelfUpdateRequestInputSchema>;

export const CreateSelfUpdateRequestResponseSchema = z.object({
  request: EmployeeSelfUpdateRequestSchema,
});
export type CreateSelfUpdateRequestResponse = z.infer<typeof CreateSelfUpdateRequestResponseSchema>;

// ---------------------------------------------------------------------------
// GET /employees/me/update-requests + GET /employees/update-requests
// ---------------------------------------------------------------------------

export const ListSelfUpdateRequestsQuerySchema = z.object({
  status: SelfUpdateRequestStatusSchema.optional(),
});
export type ListSelfUpdateRequestsQuery = z.infer<typeof ListSelfUpdateRequestsQuerySchema>;

export const ListSelfUpdateRequestsResponseSchema = z.object({
  items: z.array(EmployeeSelfUpdateRequestSchema),
});
export type ListSelfUpdateRequestsResponse = z.infer<typeof ListSelfUpdateRequestsResponseSchema>;

// ---------------------------------------------------------------------------
// PATCH /employees/update-requests/:id/approve | /reject
// ---------------------------------------------------------------------------

export const ApproveSelfUpdateRequestInputSchema = z.object({
  reviewerNote: z.string().max(1000).optional(),
});
export type ApproveSelfUpdateRequestInput = z.infer<typeof ApproveSelfUpdateRequestInputSchema>;

export const RejectSelfUpdateRequestInputSchema = z.object({
  reviewerNote: z.string().min(1).max(1000),
});
export type RejectSelfUpdateRequestInput = z.infer<typeof RejectSelfUpdateRequestInputSchema>;

export const ReviewSelfUpdateRequestResponseSchema = z.object({
  request: EmployeeSelfUpdateRequestSchema,
});
export type ReviewSelfUpdateRequestResponse = z.infer<typeof ReviewSelfUpdateRequestResponseSchema>;
